import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { reportsAPI, regionsAPI } from '../../services/api';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import { FileText, Download, Users, Calendar, TrendingUp, Filter, BarChart3, PieChart, MapPin, User } from 'lucide-react';
import { toast } from 'react-toastify';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1.5rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const PageTitle = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['2xl']};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const Tab = styled.button`
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.active ? props.theme.colors.primary : props.theme.colors.text.secondary};
  font-weight: ${props => props.active ? 600 : 400};
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const FiltersCard = styled(Card)`
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const FilterActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const StatsCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatCard = styled(Card)`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const StatCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const StatCardIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.color}20;
  color: ${props => props.color};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatCardValue = styled.div`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
`;

const StatCardLabel = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
`;

const StatCardDescription = styled.div`
  font-size: ${props => props.theme.typography.fontSize.xs};
  color: ${props => props.theme.colors.text.secondary};
  margin-top: 0.25rem;
`;

const DataTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
`;

const TableHead = styled.thead`
  background: ${props => props.theme.colors.primary}10;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${props => props.theme.colors.border.light};
  
  &:hover {
    background: ${props => props.theme.colors.background.secondary};
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TableCell = styled.td`
  padding: 1rem;
  color: ${props => props.theme.colors.text.secondary};
  font-size: ${props => props.theme.typography.fontSize.sm};
`;

const TableContainer = styled(Card)`
  padding: 1.5rem;
  overflow-x: auto;
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 1rem 0;
`;

const LoadingState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${props => props.theme.colors.text.secondary};
`;

const Reports = () => {
  const { theme } = useTheme();
  const { hasPermission } = useAuth();
  const [activeTab, setActiveTab] = useState('membership-detail');
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [regions, setRegions] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    region_id: '',
    gender: '',
    membership_status: '',
    approval_status: '',
    organization_type: '',
    search: '',
  });

  useEffect(() => {
    fetchRegions();
  }, []);

  useEffect(() => {
    // Auto-generate report when tab changes
    handleGenerateReport();
  }, [activeTab]); // Generate report when tab changes

  const fetchRegions = async () => {
    try {
      const response = await regionsAPI.getAll();
      setRegions(response.data || []);
      // Generate report after regions are loaded
      setTimeout(() => handleGenerateReport(), 100);
    } catch (error) {
      console.error('Error fetching regions:', error);
      // Still try to generate report even if regions fail
      setTimeout(() => handleGenerateReport(), 100);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => {
      const newFilters = {
        ...prev,
        [field]: value
      };
      // Auto-generate report when filters change (with debounce)
      setTimeout(() => {
        handleGenerateReport(newFilters);
      }, 500);
      return newFilters;
    });
  };

  const handleGenerateReport = async (filterValues = filters, showToast = false) => {
    try {
      setLoading(true);
      
      const params = {
        ...filterValues,
        region_id: filterValues.region_id || undefined,
        gender: filterValues.gender || undefined,
        membership_status: filterValues.membership_status || undefined,
        approval_status: filterValues.approval_status || undefined,
        organization_type: filterValues.organization_type || undefined,
      };

      // Remove empty values
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await reportsAPI.getMembersReport(params);
      setReportData(response.data);
      if (showToast) {
        toast.success('Report generated successfully');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(error.response?.data?.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'excel') => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        format,
        region_id: filters.region_id || undefined,
        gender: filters.gender || undefined,
        membership_status: filters.membership_status || undefined,
        approval_status: filters.approval_status || undefined,
        organization_type: filters.organization_type || undefined,
      };

      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await reportsAPI.exportMembers(format, params);

      // Handle blob response - axios with responseType: 'blob' returns blob in response.data
      let blob;
      if (response.data instanceof Blob) {
        blob = response.data;
      } else if (response.data && typeof response.data === 'object') {
        // If it's an object, it might be an error response
        const errorText = await new Response(response.data).text();
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.message || 'Export failed');
        } catch (e) {
          // If not JSON, create blob from the data
          blob = new Blob([response.data], { 
            type: format === 'pdf' ? 'application/pdf' : 
                  format === 'csv' ? 'text/csv' : 
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          });
        }
      } else {
        // Fallback: create blob from response data
        blob = new Blob([response.data], { 
          type: format === 'pdf' ? 'application/pdf' : 
                format === 'csv' ? 'text/csv' : 
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'pdf' ? 'pdf' : format === 'csv' ? 'csv' : 'xlsx';
        const reportTypeNames = {
          'membership-detail': 'membership_detail',
          'age-analysis': 'age_analysis',
          'gender-analysis': 'gender_analysis',
          'regional-distribution': 'regional_distribution',
          'sector-analysis': 'sector_analysis'
        };
      const fileName = `${reportTypeNames[activeTab] || 'report'}_${new Date().toISOString().split('T')[0]}.${extension}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up the URL after a short delay
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);

      toast.success(`Report exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting report:', error);
      console.error('Error response:', error.response);
      
      // Try to extract error message from blob response
      if (error.response && error.response.data instanceof Blob) {
        try {
          const errorText = await new Response(error.response.data).text();
          const errorJson = JSON.parse(errorText);
          toast.error(errorJson.message || 'Failed to export report');
        } catch (e) {
          toast.error('Failed to export report. Please try again.');
        }
      } else {
        toast.error(error.response?.data?.message || error.message || 'Failed to export report');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    const clearedFilters = {
      start_date: '',
      end_date: '',
      region_id: '',
      gender: '',
      membership_status: '',
      approval_status: '',
      organization_type: '',
      search: '',
    };
    setFilters(clearedFilters);
    // Regenerate report with cleared filters
    setTimeout(() => {
      handleGenerateReport(clearedFilters);
    }, 100);
  };

  const renderFilters = () => (
    <FiltersCard theme={theme}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
        <Filter size={18} />
        <h3 style={{ margin: 0, color: theme.colors.text.primary }}>Filters</h3>
      </div>
      <FiltersGrid>
        <Input
          type="date"
          label="Start Date"
          value={filters.start_date}
          onChange={(e) => handleFilterChange('start_date', e.target.value)}
        />
        <Input
          type="date"
          label="End Date"
          value={filters.end_date}
          onChange={(e) => handleFilterChange('end_date', e.target.value)}
        />
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            Region
          </label>
          <select
            value={filters.region_id}
            onChange={(e) => handleFilterChange('region_id', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: '8px',
              fontSize: '1rem',
            }}
          >
            <option value="">All Regions</option>
            {regions.map(region => (
              <option key={region.id} value={region.id}>{region.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            Gender
          </label>
          <select
            value={filters.gender}
            onChange={(e) => handleFilterChange('gender', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: '8px',
              fontSize: '1rem',
            }}
          >
            <option value="">All Genders</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            Membership Status
          </label>
          <select
            value={filters.membership_status}
            onChange={(e) => handleFilterChange('membership_status', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: '8px',
              fontSize: '1rem',
            }}
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            Approval Status
          </label>
          <select
            value={filters.approval_status}
            onChange={(e) => handleFilterChange('approval_status', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: '8px',
              fontSize: '1rem',
            }}
          >
            <option value="">All Statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <div>
          <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.875rem', color: theme.colors.text.secondary }}>
            Sector / Organization Type
          </label>
          <select
            value={filters.organization_type}
            onChange={(e) => handleFilterChange('organization_type', e.target.value)}
            style={{
              width: '100%',
              padding: '0.75rem',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: '8px',
              fontSize: '1rem',
            }}
          >
            <option value="">All Sectors</option>
            <option value="cso">CSO (Civil Society Organization)</option>
            <option value="ngo">NGO (Non-Governmental Organization)</option>
            <option value="government">Government</option>
            <option value="private">Private Sector</option>
            <option value="individual">Individual</option>
            <option value="other">Other</option>
          </select>
        </div>
        <Input
          type="text"
          label="Search"
          placeholder="Search by name, email, phone..."
          value={filters.search}
          onChange={(e) => handleFilterChange('search', e.target.value)}
        />
      </FiltersGrid>
      <FilterActions>
        <Button
          variant="outline"
          onClick={handleClearFilters}
        >
          Clear Filters
        </Button>
        <Button
          variant="primary"
          onClick={() => handleGenerateReport(filters, true)}
          disabled={loading}
          leftIcon={<BarChart3 size={18} />}
          leftIconTooltip="Generate Report"
        >
          {loading ? 'Generating...' : 'Refresh Report'}
        </Button>
      </FilterActions>
    </FiltersCard>
  );

  const renderMembershipDetailReport = () => {
    if (!reportData) return null;

    const summary = reportData.summary || {};
    const data = reportData.data || [];
    const detail = reportData.detail || {};

    return (
      <>
        <StatsCardsGrid>
          <StatCard theme={theme}>
            <StatCardHeader>
              <div>
                <StatCardLabel theme={theme}>Total Members</StatCardLabel>
                <StatCardValue theme={theme}>{summary.total_members || 0}</StatCardValue>
                <StatCardDescription theme={theme}>All registered members in the system</StatCardDescription>
              </div>
              <StatCardIcon color={theme.colors.primary}>
                <Users size={24} />
              </StatCardIcon>
            </StatCardHeader>
          </StatCard>

          <StatCard theme={theme}>
            <StatCardHeader>
              <div>
                <StatCardLabel theme={theme}>Active Members</StatCardLabel>
                <StatCardValue theme={theme}>{summary.active_members || 0}</StatCardValue>
                <StatCardDescription theme={theme}>Currently active membership status</StatCardDescription>
              </div>
              <StatCardIcon color={theme.colors.success}>
                <TrendingUp size={24} />
              </StatCardIcon>
            </StatCardHeader>
          </StatCard>

          <StatCard theme={theme}>
            <StatCardHeader>
              <div>
                <StatCardLabel theme={theme}>Pending Approval</StatCardLabel>
                <StatCardValue theme={theme}>{summary.pending_approval || 0}</StatCardValue>
                <StatCardDescription theme={theme}>Members awaiting approval</StatCardDescription>
              </div>
              <StatCardIcon color={theme.colors.warning}>
                <Calendar size={24} />
              </StatCardIcon>
            </StatCardHeader>
          </StatCard>

          <StatCard theme={theme}>
            <StatCardHeader>
              <div>
                <StatCardLabel theme={theme}>Female Members</StatCardLabel>
                <StatCardValue theme={theme}>{summary.female_members || 0}</StatCardValue>
                <StatCardDescription theme={theme}>Total female members</StatCardDescription>
              </div>
              <StatCardIcon color={theme.colors.info}>
                <User size={24} />
              </StatCardIcon>
            </StatCardHeader>
          </StatCard>

          <StatCard theme={theme}>
            <StatCardHeader>
              <div>
                <StatCardLabel theme={theme}>Male Members</StatCardLabel>
                <StatCardValue theme={theme}>{summary.male_members || 0}</StatCardValue>
                <StatCardDescription theme={theme}>Total male members</StatCardDescription>
              </div>
              <StatCardIcon color={theme.colors.secondary}>
                <User size={24} />
              </StatCardIcon>
            </StatCardHeader>
          </StatCard>
        </StatsCardsGrid>

        <TableContainer theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <SectionTitle theme={theme}>Membership Details ({detail.total || data.length} records)</SectionTitle>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={loading}
              >
                PDF
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('csv')}
                disabled={loading}
              >
                CSV
              </Button>
            </div>
          </div>
          <DataTable theme={theme}>
            <TableHead theme={theme}>
              <TableRow>
                <TableHeader theme={theme}>Member ID</TableHeader>
                <TableHeader theme={theme}>Full Name</TableHeader>
                <TableHeader theme={theme}>Gender</TableHeader>
                <TableHeader theme={theme}>Age</TableHeader>
                <TableHeader theme={theme}>Region</TableHeader>
                <TableHeader theme={theme}>Email</TableHeader>
                <TableHeader theme={theme}>Phone</TableHeader>
                <TableHeader theme={theme}>Membership Type</TableHeader>
                <TableHeader theme={theme}>Membership Status</TableHeader>
                <TableHeader theme={theme}>Approval Status</TableHeader>
                <TableHeader theme={theme}>Membership Date</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {data.slice(0, 100).map((member, index) => (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>{member.member_id || member.registration_no || '—'}</TableCell>
                  <TableCell theme={theme}>{member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || '—'}</TableCell>
                  <TableCell theme={theme}>{member.gender || '—'}</TableCell>
                  <TableCell theme={theme}>{member.age || '—'}</TableCell>
                  <TableCell theme={theme}>{member.region || '—'}</TableCell>
                  <TableCell theme={theme}>{member.email || '—'}</TableCell>
                  <TableCell theme={theme}>{member.phone || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_type || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_status || '—'}</TableCell>
                  <TableCell theme={theme}>{member.approval_status || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_date || '—'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </DataTable>
          {data.length > 100 && (
            <div style={{ marginTop: '1rem', textAlign: 'center', color: theme.colors.text.secondary }}>
              Showing first 100 of {data.length} records. Export to see all records.
            </div>
          )}
        </TableContainer>
      </>
    );
  };

  const renderAgeAnalysisReport = () => {
    if (!reportData) return null;

    const ageBreakdown = reportData.age_breakdown || {};
    const bands = ageBreakdown.bands || [];
    const rows = ageBreakdown.rows || [];

    return (
      <>
        <StatsCardsGrid>
          {bands.map((band, index) => (
            <StatCard key={index} theme={theme}>
              <StatCardHeader>
                <div>
                  <StatCardLabel theme={theme}>{band.band}</StatCardLabel>
                  <StatCardValue theme={theme}>{band.count}</StatCardValue>
                  <StatCardDescription theme={theme}>{band.percentage}% of total members</StatCardDescription>
                </div>
                <StatCardIcon color={theme.colors.primary}>
                  <User size={24} />
                </StatCardIcon>
              </StatCardHeader>
            </StatCard>
          ))}
        </StatsCardsGrid>

        <TableContainer theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <SectionTitle theme={theme}>Age Group Details ({rows.length} records)</SectionTitle>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={loading}
              >
                PDF
              </Button>
            </div>
          </div>
          <DataTable theme={theme}>
            <TableHead theme={theme}>
              <TableRow>
                <TableHeader theme={theme}>Member ID</TableHeader>
                <TableHeader theme={theme}>Full Name</TableHeader>
                <TableHeader theme={theme}>Gender</TableHeader>
                <TableHeader theme={theme}>Age</TableHeader>
                <TableHeader theme={theme}>Age Band</TableHeader>
                <TableHeader theme={theme}>Region</TableHeader>
                <TableHeader theme={theme}>Membership Status</TableHeader>
                <TableHeader theme={theme}>Membership Type</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {rows.slice(0, 100).map((member, index) => (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>{member.member_id || '—'}</TableCell>
                  <TableCell theme={theme}>{member.full_name || '—'}</TableCell>
                  <TableCell theme={theme}>{member.gender || '—'}</TableCell>
                  <TableCell theme={theme}>{member.age || '—'}</TableCell>
                  <TableCell theme={theme}>{member.age_band || '—'}</TableCell>
                  <TableCell theme={theme}>{member.region || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_status || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_type || '—'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </DataTable>
          {rows.length > 100 && (
            <div style={{ marginTop: '1rem', textAlign: 'center', color: theme.colors.text.secondary }}>
              Showing first 100 of {rows.length} records. Export to see all records.
            </div>
          )}
        </TableContainer>
      </>
    );
  };

  const renderGenderAnalysisReport = () => {
    if (!reportData) return null;

    const genderBreakdown = reportData.gender_breakdown || {};
    const groups = genderBreakdown.groups || [];
    const rows = genderBreakdown.rows || [];

    return (
      <>
        <StatsCardsGrid>
          {groups.map((group, index) => (
            <StatCard key={index} theme={theme}>
              <StatCardHeader>
                <div>
                  <StatCardLabel theme={theme}>{group.gender}</StatCardLabel>
                  <StatCardValue theme={theme}>{group.count}</StatCardValue>
                  <StatCardDescription theme={theme}>{group.percentage}% of total members</StatCardDescription>
                </div>
                <StatCardIcon color={theme.colors.primary}>
                  <User size={24} />
                </StatCardIcon>
              </StatCardHeader>
            </StatCard>
          ))}
        </StatsCardsGrid>

        <TableContainer theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <SectionTitle theme={theme}>Gender-Based Member Details ({rows.length} records)</SectionTitle>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={loading}
              >
                PDF
              </Button>
            </div>
          </div>
          <DataTable theme={theme}>
            <TableHead theme={theme}>
              <TableRow>
                <TableHeader theme={theme}>Member ID</TableHeader>
                <TableHeader theme={theme}>Full Name</TableHeader>
                <TableHeader theme={theme}>Gender</TableHeader>
                <TableHeader theme={theme}>Age</TableHeader>
                <TableHeader theme={theme}>Age Band</TableHeader>
                <TableHeader theme={theme}>Region</TableHeader>
                <TableHeader theme={theme}>Membership Status</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {rows.slice(0, 100).map((member, index) => (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>{member.member_id || '—'}</TableCell>
                  <TableCell theme={theme}>{member.full_name || '—'}</TableCell>
                  <TableCell theme={theme}>{member.gender || '—'}</TableCell>
                  <TableCell theme={theme}>{member.age || '—'}</TableCell>
                  <TableCell theme={theme}>{member.age_band || '—'}</TableCell>
                  <TableCell theme={theme}>{member.region || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_status || '—'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </DataTable>
          {rows.length > 100 && (
            <div style={{ marginTop: '1rem', textAlign: 'center', color: theme.colors.text.secondary }}>
              Showing first 100 of {rows.length} records. Export to see all records.
            </div>
          )}
        </TableContainer>
      </>
    );
  };

  const renderRegionalDistributionReport = () => {
    if (!reportData) return null;

    const regionBreakdown = reportData.region_breakdown || {};
    const regions = regionBreakdown.regions || [];
    const rows = regionBreakdown.rows || [];

    return (
      <>
        <StatsCardsGrid>
          {regions.slice(0, 10).map((region, index) => (
            <StatCard key={index} theme={theme}>
              <StatCardHeader>
                <div>
                  <StatCardLabel theme={theme}>{region.region}</StatCardLabel>
                  <StatCardValue theme={theme}>{region.count}</StatCardValue>
                  <StatCardDescription theme={theme}>{region.percentage}% of total members</StatCardDescription>
                </div>
                <StatCardIcon color={theme.colors.primary}>
                  <MapPin size={24} />
                </StatCardIcon>
              </StatCardHeader>
            </StatCard>
          ))}
        </StatsCardsGrid>

        <TableContainer theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <SectionTitle theme={theme}>Regional Member Details ({rows.length} records)</SectionTitle>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={loading}
              >
                PDF
              </Button>
            </div>
          </div>
          <DataTable theme={theme}>
            <TableHead theme={theme}>
              <TableRow>
                <TableHeader theme={theme}>Member ID</TableHeader>
                <TableHeader theme={theme}>Full Name</TableHeader>
                <TableHeader theme={theme}>Region</TableHeader>
                <TableHeader theme={theme}>Membership Status</TableHeader>
                <TableHeader theme={theme}>Membership Type</TableHeader>
                <TableHeader theme={theme}>Gender</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {rows.slice(0, 100).map((member, index) => (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>{member.member_id || '—'}</TableCell>
                  <TableCell theme={theme}>{member.full_name || '—'}</TableCell>
                  <TableCell theme={theme}>{member.region || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_status || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_type || '—'}</TableCell>
                  <TableCell theme={theme}>{member.gender || '—'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </DataTable>
          {rows.length > 100 && (
            <div style={{ marginTop: '1rem', textAlign: 'center', color: theme.colors.text.secondary }}>
              Showing first 100 of {rows.length} records. Export to see all records.
            </div>
          )}
        </TableContainer>
      </>
    );
  };

  const renderSectorAnalysisReport = () => {
    if (!reportData) return null;

    const data = reportData.data || [];
    
    // Calculate sector breakdown from data
    const sectorMap = {};
    data.forEach(member => {
      const sector = member.organization_type || 'Unspecified';
      if (!sectorMap[sector]) {
        sectorMap[sector] = [];
      }
      sectorMap[sector].push(member);
    });

    const sectorBreakdown = Object.entries(sectorMap).map(([sector, members]) => {
      const total = data.length || 1;
      return {
        sector: sector === 'Unspecified' ? 'Unspecified' : sector.charAt(0).toUpperCase() + sector.slice(1),
        count: members.length,
        percentage: ((members.length / total) * 100).toFixed(1),
        members: members
      };
    }).sort((a, b) => b.count - a.count);

    const getSectorLabel = (sector) => {
      const labels = {
        'cso': 'CSO (Civil Society Organization)',
        'ngo': 'NGO (Non-Governmental Organization)',
        'government': 'Government',
        'private': 'Private Sector',
        'individual': 'Individual',
        'other': 'Other',
        'Unspecified': 'Unspecified'
      };
      return labels[sector.toLowerCase()] || sector;
    };

    return (
      <>
        <StatsCardsGrid>
          {sectorBreakdown.map((sector, index) => (
            <StatCard key={index} theme={theme}>
              <StatCardHeader>
                <div>
                  <StatCardLabel theme={theme}>{getSectorLabel(sector.sector)}</StatCardLabel>
                  <StatCardValue theme={theme}>{sector.count}</StatCardValue>
                  <StatCardDescription theme={theme}>{sector.percentage}% of total members</StatCardDescription>
                </div>
                <StatCardIcon color={theme.colors.primary}>
                  <BarChart3 size={24} />
                </StatCardIcon>
              </StatCardHeader>
            </StatCard>
          ))}
        </StatsCardsGrid>

        <TableContainer theme={theme}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <SectionTitle theme={theme}>Sector-Based Member Details ({data.length} records)</SectionTitle>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('excel')}
                disabled={loading}
              >
                Excel
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('pdf')}
                disabled={loading}
              >
                PDF
              </Button>
              <Button
                variant="outline"
                size="small"
                leftIcon={<Download size={16} />}
                onClick={() => handleExport('csv')}
                disabled={loading}
              >
                CSV
              </Button>
            </div>
          </div>
          <DataTable theme={theme}>
            <TableHead theme={theme}>
              <TableRow>
                <TableHeader theme={theme}>Member ID</TableHeader>
                <TableHeader theme={theme}>Full Name</TableHeader>
                <TableHeader theme={theme}>Sector / Organization Type</TableHeader>
                <TableHeader theme={theme}>Organization Name</TableHeader>
                <TableHeader theme={theme}>Position Title</TableHeader>
                <TableHeader theme={theme}>Region</TableHeader>
                <TableHeader theme={theme}>Gender</TableHeader>
                <TableHeader theme={theme}>Email</TableHeader>
                <TableHeader theme={theme}>Phone</TableHeader>
                <TableHeader theme={theme}>Membership Status</TableHeader>
              </TableRow>
            </TableHead>
            <tbody>
              {data.slice(0, 100).map((member, index) => (
                <TableRow key={index} theme={theme}>
                  <TableCell theme={theme}>{member.member_id || member.registration_no || '—'}</TableCell>
                  <TableCell theme={theme}>{member.full_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || '—'}</TableCell>
                  <TableCell theme={theme}>
                    {member.organization_type ? getSectorLabel(member.organization_type) : '—'}
                  </TableCell>
                  <TableCell theme={theme}>{member.organization_name || '—'}</TableCell>
                  <TableCell theme={theme}>{member.position_title || '—'}</TableCell>
                  <TableCell theme={theme}>{member.region || '—'}</TableCell>
                  <TableCell theme={theme}>{member.gender || '—'}</TableCell>
                  <TableCell theme={theme}>{member.email || '—'}</TableCell>
                  <TableCell theme={theme}>{member.phone || '—'}</TableCell>
                  <TableCell theme={theme}>{member.membership_status || '—'}</TableCell>
                </TableRow>
              ))}
            </tbody>
          </DataTable>
          {data.length > 100 && (
            <div style={{ marginTop: '1rem', textAlign: 'center', color: theme.colors.text.secondary }}>
              Showing first 100 of {data.length} records. Export to see all records.
            </div>
          )}
        </TableContainer>
      </>
    );
  };

  if (!hasPermission('view_reports')) {
    return (
      <PageContainer theme={theme}>
        <EmptyState theme={theme}>
          <p>You do not have permission to view reports.</p>
        </EmptyState>
      </PageContainer>
    );
  }

  return (
    <PageContainer theme={theme}>
      <PageHeader>
        <PageTitle theme={theme}>Reports & Analytics</PageTitle>
      </PageHeader>

      <TabsContainer theme={theme}>
        <Tab
          theme={theme}
          active={activeTab === 'membership-detail'}
          onClick={() => {
            setActiveTab('membership-detail');
          }}
        >
          <FileText size={18} />
          Membership Detail Report
        </Tab>
        <Tab
          theme={theme}
          active={activeTab === 'age-analysis'}
          onClick={() => {
            setActiveTab('age-analysis');
          }}
        >
          <BarChart3 size={18} />
          Age Analysis Report
        </Tab>
        <Tab
          theme={theme}
          active={activeTab === 'gender-analysis'}
          onClick={() => {
            setActiveTab('gender-analysis');
          }}
        >
          <PieChart size={18} />
          Gender Analysis Report
        </Tab>
        <Tab
          theme={theme}
          active={activeTab === 'regional-distribution'}
          onClick={() => {
            setActiveTab('regional-distribution');
          }}
        >
          <MapPin size={18} />
          Regional Distribution Report
        </Tab>
        <Tab
          theme={theme}
          active={activeTab === 'sector-analysis'}
          onClick={() => {
            setActiveTab('sector-analysis');
          }}
        >
          <BarChart3 size={18} />
          Sector Analysis Report
        </Tab>
      </TabsContainer>

      {renderFilters()}

      {loading && (
        <LoadingState theme={theme}>Generating report...</LoadingState>
      )}

      {!loading && reportData && (
        <>
          {activeTab === 'membership-detail' && renderMembershipDetailReport()}
          {activeTab === 'age-analysis' && renderAgeAnalysisReport()}
          {activeTab === 'gender-analysis' && renderGenderAnalysisReport()}
          {activeTab === 'regional-distribution' && renderRegionalDistributionReport()}
          {activeTab === 'sector-analysis' && renderSectorAnalysisReport()}
        </>
      )}

      {!loading && !reportData && (
        <EmptyState theme={theme}>
          <p>Use the filters above to generate a report.</p>
        </EmptyState>
      )}
    </PageContainer>
  );
};

export default Reports;
